import { useEffect, useState } from 'react';
import { SLOT_THEMES, SLOT_PAYLINES } from '@raketta/shared';
import { useAppStore } from '../store/useAppStore';
import { slotsApi, SlotSpinResult, SpinEval, Tier } from '../api/slots';
import { SlotSymbol } from '../components/SlotSymbol';
import { ReelGrid } from '../components/ReelGrid';
import { telegram } from '../lib/telegram';
import { sound } from '../lib/sound';
import { formatTon } from '../lib/format';
import { replayLink } from '../lib/appConfig';

const REELS = 5;
const ROWS = 3;
const POOL: Tier[] = ['h1', 'h2', 'm1', 'm2', 'l1', 'l2', 'l2', 'l1', 'wild', 'scatter'];
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const randTier = (): Tier => POOL[Math.floor(Math.random() * POOL.length)];
const randGrid = (): Tier[][] =>
  Array.from({ length: REELS }, () => Array.from({ length: ROWS }, randTier));
const STOP_MS = REELS * 150 + 350;

export interface SlotReplay {
  base: SpinEval;
  free: SpinEval[];
  totalWin: number;
}

function winCellsOf(spin: SpinEval): Set<string> {
  const set = new Set<string>();
  spin.lineWins.forEach((w) => {
    const rows = SLOT_PAYLINES[w.line];
    for (let reel = 0; reel < w.count; reel++) set.add(`${reel}-${rows[reel]}`);
  });
  return set;
}

export function SlotsScreen({ replay, shareId }: { replay?: SlotReplay; shareId?: number } = {}) {
  const balance = useAppStore((s) => s.balance);
  const setBalance = useAppStore((s) => s.setBalance);
  const showToast = useAppStore((s) => s.showToast);

  const [theme, setTheme] = useState('fruits');
  const [amount, setAmount] = useState('10');
  const [grid, setGrid] = useState<Tier[][]>(() => randGrid());
  const [spinning, setSpinning] = useState(false);
  const [winCells, setWinCells] = useState<Set<string>>(new Set());
  const [winText, setWinText] = useState<string | null>(null);
  const [freeInfo, setFreeInfo] = useState<{ idx: number; total: number } | null>(null);
  const [history, setHistory] = useState<{ betId: number; payout: string; amount: string }[]>([]);
  const [soundOn, setSoundOn] = useState(true);

  const isReplay = !!replay;
  const balanceNum = parseFloat(balance) || 0;
  const amountNum = parseFloat(amount) || 0;
  const canSpin = !spinning && amountNum > 0 && amountNum <= balanceNum;

  useEffect(() => {
    if (isReplay) return;
    slotsApi.history(12).then((h: any) => setHistory(h)).catch(() => undefined);
  }, [isReplay]);

  const setAmountSafe = (v: number) => {
    const c = Math.max(0, Math.min(v, balanceNum || v));
    setAmount(c % 1 === 0 ? String(c) : c.toFixed(2));
  };

  const reveal = (ev: SpinEval) => {
    setGrid(ev.grid);
    setWinCells(winCellsOf(ev));
    setSpinning(false);
    if (ev.totalWin > 0) sound.win();
  };

  const animate = async (base: SpinEval, free: SpinEval[]) => {
    reveal(base);
    if (base.totalWin > 0) setWinText(`+${formatTon(base.totalWin)}`);
    await sleep(STOP_MS);
    if (free.length > 0) {
      sound.freeSpins();
      let acc = base.totalWin;
      for (let i = 0; i < free.length; i++) {
        setFreeInfo({ idx: i + 1, total: free.length });
        setWinCells(new Set());
        setSpinning(true);
        sound.spin();
        await sleep(500);
        acc += free[i].totalWin;
        reveal(free[i]);
        if (free[i].totalWin > 0) setWinText(`ФРИСПИН · всего +${formatTon(acc)}`);
        await sleep(STOP_MS);
      }
      setFreeInfo(null);
    }
  };

  useEffect(() => {
    if (!replay) return;
    let cancelled = false;
    (async () => {
      sound.resume();
      sound.spin();
      setWinCells(new Set());
      setWinText(null);
      setFreeInfo(null);
      setSpinning(true);
      await sleep(850);
      if (cancelled) return;
      await animate(replay.base, replay.free);
      if (cancelled) return;
      if (replay.totalWin > 0) {
        setWinText(`+${formatTon(replay.totalWin)}`);
        sound.coin();
      }
      setSpinning(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const spin = async () => {
    if (!canSpin) {
      if (amountNum > balanceNum) showToast('Недостаточно средств', true);
      return;
    }
    telegram.haptic('medium');
    sound.resume();
    sound.spin();
    setWinCells(new Set());
    setWinText(null);
    setFreeInfo(null);
    setSpinning(true);

    const start = performance.now();
    try {
      const res: SlotSpinResult = await slotsApi.spin(amount, theme);
      const elapsed = performance.now() - start;
      if (elapsed < 850) await sleep(850 - elapsed);

      await animate(res.base, res.free);

      const total = parseFloat(res.totalWin);
      setBalance(res.newBalance);
      setHistory((h) => [{ betId: res.betId, payout: res.totalWin, amount: res.amount }, ...h].slice(0, 12));
      if (total > 0) {
        setWinText(`+${formatTon(res.totalWin)}`);
        if (total >= amountNum * 15) sound.bigWin();
        else sound.coin();
        showToast(`Выигрыш +${formatTon(res.totalWin)}`);
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Ошибка спина';
      showToast(Array.isArray(msg) ? msg[0] : msg, true);
      setSpinning(false);
    } finally {
      setSpinning(false);
      setFreeInfo(null);
    }
  };

  return (
    <>
      {!isReplay && (
        <div className="recent">
          {SLOT_THEMES.map((t) => (
            <button
              key={t.id}
              className={`slot-theme ${theme === t.id ? 'active' : ''}`}
              disabled={spinning}
              onClick={() => setTheme(t.id)}
            >
              {t.name}
            </button>
          ))}
        </div>
      )}

      <div className="card slot-board">
        {freeInfo && <div className="slot-free-badge">ФРИСПИНЫ {freeInfo.idx}/{freeInfo.total}</div>}
        <ReelGrid
          reels={REELS}
          rows={ROWS}
          grid={grid}
          spinning={spinning}
          winCells={winCells}
          randomCell={randTier}
          renderCell={(tier) => <SlotSymbol theme={theme} tier={tier} />}
        />
        <div className={`slot-win ${winText ? 'show' : ''}`}>{winText ?? ' '}</div>
      </div>

      {isReplay ? (
        <div className="card replay-foot">
          <span className="replay-tag">ПОВТОР ВЫИГРЫША</span>
          <button
            className="btn"
            disabled={spinning}
            onClick={() =>
              telegram.share(
                `Зацени реплей: +${formatTon(replay!.totalWin)} в слотах — 1Zov`,
                shareId ? replayLink(shareId) : undefined
              )
            }
          >
            Поделиться
          </button>
        </div>
      ) : (
        <>
          <div className="card bet">
            <div className="label-row">
              <span className="label">Ставка (на 10 линий)</span>
              <span className="muted" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                баланс {formatTon(balance)}
                <button className="snd" onClick={() => setSoundOn(sound.toggle())}>{soundOn ? '♪' : '×'}</button>
              </span>
            </div>
            <div className="field">
              <span className="gem">◆</span>
              <input
                inputMode="decimal"
                value={amount}
                disabled={spinning}
                onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
              />
              <div className="mini-actions">
                <button onClick={() => setAmountSafe(amountNum / 2)} disabled={spinning}>½</button>
                <button onClick={() => setAmountSafe(amountNum * 2)} disabled={spinning}>2×</button>
                <button onClick={() => setAmountSafe(balanceNum)} disabled={spinning}>MAX</button>
              </div>
            </div>
            <button className={`btn ${spinning ? 'loading' : ''}`} disabled={!canSpin} onClick={spin}>
              {spinning ? <span className="spin" /> : 'Крутить'}
            </button>
          </div>

          {history.length > 0 && (
            <div className="recent">
              {history.map((h) => {
                const win = parseFloat(h.payout) > 0;
                return (
                  <span key={h.betId} className={`pill ${win ? 'win' : 'lose'} static`}>
                    {win ? `+${formatTon(h.payout)}` : `−${formatTon(h.amount)}`}
                  </span>
                );
              })}
            </div>
          )}
        </>
      )}
    </>
  );
}
